/**
 * Ticket write endpoints for odl_app.
 *
 * Support tickets are browsed via EntityGrid (dataSource/apiClient) — there is
 * no list endpoint here. This module injects the odl-app Ticket read/write
 * surfaces into entitiesApi and re-exports the generated hooks for component use:
 *   - retrieve a single ticket (detail read)
 *   - create a ticket (body may carry needs_rma:true → the backend opens the
 *     RMA in the same atomic)
 *   - patch a ticket (a status change closes the ticket; needs_rma honored here too)
 *
 * All endpoints live under /api/odl-app/tickets/, so they ride the shared
 * axiosBaseQuery (plain `query`) — no queryFn escape hatch needed. Responses are
 * wrapped in the odl-app { "data": … } envelope, so transformResponse is the
 * plain `b => b.data` (never `?? b`, which is only for bare RPC/bulk endpoints).
 *
 * Tags invalidate the Ticket LIST so any future RTKQ read consumer refetches.
 * The EntityGrid list is on the MUI dataSource (apiClient), which tag
 * invalidation does NOT reach — grid callers must still refresh the grid
 * themselves after a mutation resolves.
 *
 * Contains:
 * - ticketsApi      — the injected endpoint slice
 * - hook re-exports — useRetrieveTicketQuery, useCreateTicketMutation,
 *                     usePatchTicketMutation
 */

import { entitiesApi } from './entitiesApi';

const ticketsApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Retrieve a single ticket (TicketDetailSerializer shape).
     *
     * @param {{ id: string|number }} arg
     */
    retrieveTicket: build.query({
      query: ({ id }) => ({ url: `tickets/${id}/` }),
      transformResponse: b => b.data,
      providesTags: (result, error, { id }) => [{ type: 'Ticket', id }],
    }),

    /**
     * Create a ticket. Body is TicketSerializer input; pass needs_rma:true to
     * have the backend open the RMA in the same atomic. Returns detail (201).
     *
     * @param {{ body: object }} arg
     */
    createTicket: build.mutation({
      query: ({ body }) => ({ url: 'tickets/', method: 'post', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'Ticket', id: 'LIST' }],
    }),

    /**
     * Partial update a ticket (a status change closes it; needs_rma honored).
     *
     * @param {{ id: string|number, body: object }} arg
     */
    patchTicket: build.mutation({
      query: ({ id, body }) => ({
        url: `tickets/${id}/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Ticket', id: 'LIST' },
        { type: 'Ticket', id },
      ],
    }),
  }),
});

export { ticketsApi };
export const { useRetrieveTicketQuery, useCreateTicketMutation, usePatchTicketMutation } = ticketsApi;
